import { memo, useCallback, useState } from 'react'
import { CSSObjectWithLabel } from 'react-select'
import { ascending, distinct, entity, like, notEquals, notNull, query, string } from '~/prix'
import SelectFromQuery from '~/prix/react/components/form/inputs/selectFromQuery'

interface SelectRegionalFilterProps {
  onSelectRegional: (regional: { regional: string } | null) => void
  placeholder?: string
  value?: { regional: string } | null
}

const DEBUG_REGIONAL_FILTER = false

const debugRegionalFilter = (message: string, payload?: unknown) => {
  if (!DEBUG_REGIONAL_FILTER) return
  console.info(`[sentinel][regional-filter] ${message}`, payload ?? '')
}

const SelectRegionalFilter = ({
  onSelectRegional,
  placeholder = 'Selecione uma regional',
  value,
}: SelectRegionalFilterProps) => {
  const [isDisabled, setIsDisabled] = useState(false)

  const regionalListQueryFactory = useCallback(
    (input: string) => {
      debugRegionalFilter('queryFactory called', {
        input,
        wildcardInput: `%${input.replace(/\s/g, '%')}%`,
      })
      return query('atendimento', { schema: 'appsebrae' })
        .select({
          regional: distinct(entity('atendimento', { schema: 'appsebrae' }).property('regional')),
        })
        .where(
          notNull(entity('atendimento', { schema: 'appsebrae' }).property('regional')),
          notEquals(
            entity('atendimento', { schema: 'appsebrae' }).property('regional'),
            string().value(''),
          ),
          like(
            entity('atendimento', { schema: 'appsebrae' }).property('regional'),
            string().value(`%${input.replace(/\s/g, '%')}%`),
          ),
        )
        .order(ascending('regional'))
        .limit(1000)
    },
    [],
  )

  const normalizeRegionalOptions = useCallback(
    (items: Array<{ regional: string | null }>) => {
      debugRegionalFilter('raw options returned', {
        rawCount: items.length,
        rawPreview: items.slice(0, 8),
      })
      const uniqueRegionals = new Set<string>()
      const normalized = items
        .map(item => ({ regional: item.regional?.trim() || '' }))
        .filter(item => item.regional.length > 0)
        .filter(item => {
          if (uniqueRegionals.has(item.regional)) return false
          uniqueRegionals.add(item.regional)
          return true
        })
        .slice(0, 50)

      debugRegionalFilter('options after normalization', {
        normalizedCount: normalized.length,
        normalizedPreview: normalized.slice(0, 8),
      })

      return normalized
    },
    [],
  )

  const handleChange = useCallback(
    (selectedRegional: { regional: string } | null) => {
      debugRegionalFilter('user selected option', selectedRegional)
      if (selectedRegional?.regional) {
        onSelectRegional({
          regional: selectedRegional.regional,
        })
        setIsDisabled(true)
      }
    },
    [onSelectRegional],
  )

  return (
    <SelectFromQuery
      queryFactory={regionalListQueryFactory}
      idProperty='regional'
      labelProperty='regional'
      placeholder={placeholder}
      value={value ?? undefined}
      debugName='regional-filter'
      sourceKey='oltp'
      onDirectChange={handleChange}
      onQueryComplete={normalizeRegionalOptions}
      isDisabled={isDisabled}
      isClearable={false}
      debounceMs={250}
      minimumSearchLength={0}
      styles={{
        control: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          width: '150px',
          minHeight: '32px',
          fontSize: '0.9rem',
          cursor: 'pointer',
          '@media (max-width: 768px)': {
            width: '100%',
          },
        }),
        valueContainer: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          maxHeight: '30px',
          padding: '0 6px',
        }),
        input: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          fontSize: '0.9rem',
        }),
        placeholder: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          fontSize: '0.9rem',
        }),
        singleValue: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          fontSize: '0.9rem',
        }),
        option: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          fontSize: '0.9rem',
        }),
        menuList: (baseStyles: CSSObjectWithLabel) => ({
          ...baseStyles,
          maxHeight: '150px',
          overflowX: 'hidden',
        }),
      }}
    />
  )
}

export default memo(SelectRegionalFilter)
